import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  Gavel,
  Loader2,
  Scale,
  ShieldQuestion,
} from 'lucide-react';
import {
  Agreement,
  BlindCandidate,
  DisputedCandidate,
  adjudicateCandidate,
  adjudicationQueue,
  corpusAgreement,
  corpusQueue,
  submitSecondRead,
} from '../admin';

/**
 * The corpus admission gate (DESIGN.md §11.4, §11.3(c), §12.7 stage 5).
 *
 * **This screen is built around what it refuses to show.** A candidate waiting for its second read
 * is rendered without the submitted label and without the submitter's name, and the API does not
 * send either — so there is nothing here to accidentally reveal. Taking the second reader's own
 * label *first* is the entire mechanism: reversed, it would be a confirmation step, inter-reader
 * agreement would measure suggestibility rather than difficulty, and §11.4's argument for staffing
 * the gate would collapse.
 *
 * Two things the UI states rather than implies, both because a clinician should not have to infer
 * them from an empty screen:
 *
 * - submitting a strip does **not** add it to the corpus;
 * - a disagreement is not a failure. It is the most valuable record the gate produces, because a
 *   strip two qualified readers label differently is by definition a hard case, and §11.3(3) warns
 *   those are exactly what a curated corpus otherwise lacks.
 */

interface CorpusReviewProps {
  onBack: () => void;
  /** Open the blinded audit for a study. Same role, same job (§11.3(b)). */
  onOpenAudit?: (studyId: string) => void;
}

/** Classes a reader may assign. Kept short: a long list invites a guess over a judgement. */
const LABELS = [
  'afib',
  'aflutter',
  'vt',
  'svt',
  'pause',
  'bradycardia',
  'tachycardia',
  'artifact',
  'normal',
];

const seconds = (value: number) => {
  const total = Math.max(0, Math.round(value));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
    : `${m}m ${String(s).padStart(2, '0')}s`;
};

const LabelPicker: React.FC<{
  value: string | null;
  onChange: (label: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <div className="flex flex-wrap gap-1.5">
    {LABELS.map((label) => (
      <button
        key={label}
        type="button"
        disabled={disabled}
        onClick={() => onChange(label)}
        className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition ${
          value === label
            ? 'border-cyan-500 bg-cyan-900 text-white'
            : 'border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500'
        } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        {label}
      </button>
    ))}
  </div>
);

export const CorpusReview: React.FC<CorpusReviewProps> = ({ onBack, onOpenAudit }) => {
  const [pending, setPending] = useState<BlindCandidate[]>([]);
  const [disputed, setDisputed] = useState<DisputedCandidate[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<
    Record<string, { agreed: boolean; submitted: string; second: string }>
  >({});
  const [adjudication, setAdjudication] = useState<
    Record<string, { label: string | null; reason: string }>
  >({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [queue, disputes, agreed] = await Promise.all([
        corpusQueue(),
        adjudicationQueue(),
        corpusAgreement(),
      ]);
      setPending(queue.candidates);
      setCounts(queue.counts);
      setDisputed(disputes.candidates);
      setAgreement(agreed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the corpus queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const recordSecondRead = async (candidate: BlindCandidate) => {
    const label = chosen[candidate.id];
    if (!label) return;
    setBusyId(candidate.id);
    setError(null);
    try {
      const result = await submitSecondRead(candidate.id, label);
      // Revealed only now, and only for this card: the reader learns whether they agreed after
      // their own judgement is recorded and can no longer be influenced by the answer.
      setRevealed((r) => ({
        ...r,
        [candidate.id]: {
          agreed: result.agreed,
          submitted: result.submitted_label,
          second: result.second_label,
        },
      }));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the read.');
    } finally {
      setBusyId(null);
    }
  };

  const settle = async (candidate: DisputedCandidate, admit: boolean) => {
    const state = adjudication[candidate.id];
    if (!state?.reason?.trim()) {
      setError('Record why. An unexplained decision is not an audit trail.');
      return;
    }
    if (admit && !state.label) {
      setError('Admitting a candidate needs the adjudicated label.');
      return;
    }
    setBusyId(candidate.id);
    setError(null);
    try {
      await adjudicateCandidate(candidate.id, {
        admit,
        label: state.label ?? undefined,
        reason: state.reason.trim(),
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not adjudicate.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-6">
      <div className="glass-panel flex items-center justify-between rounded-2xl p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="rounded-xl border border-slate-800 bg-slate-900 p-2 text-slate-300 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-lg font-bold text-white">Corpus review</h2>
            <p className="text-xs text-slate-400">
              Label each strip before seeing what the submitter said. That order is what makes the
              agreement number mean anything.
            </p>
          </div>
        </div>
        {agreement?.agreement != null && (
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Scale className="h-3.5 w-3.5" /> Inter-reader agreement
            </div>
            <div className="text-lg font-bold text-white">
              {(agreement.agreement * 100).toFixed(0)}%
            </div>
            <div className="text-[11px] text-slate-500">over {agreement.read_twice} read twice</div>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">
          {error}
        </div>
      )}

      {agreement?.agreement != null && agreement.read_twice > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2 text-[11px] text-slate-400">
          The engine cannot meaningfully be scored better than two qualified readers agree with each
          other. This figure is the ceiling on any accuracy claim made from this corpus.
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <EyeOff className="h-4 w-4 text-cyan-400" />
              <h3 className="text-sm font-semibold text-white">
                Awaiting a second read
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {counts.PENDING ?? 0} pending
                </span>
              </h3>
            </div>

            {pending.length === 0 && (
              <p className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-xs text-slate-400">
                Nothing waiting. Submissions from your own account are excluded, since a second read
                by the submitter would measure only their own consistency.
              </p>
            )}

            {pending.map((candidate) => {
              const reveal = revealed[candidate.id];
              return (
                <div key={candidate.id} className="glass-panel space-y-3 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-xs text-slate-300">
                        {seconds(candidate.start_seconds)} – {seconds(candidate.end_seconds)}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {(candidate.strata?.device_model as string) ?? 'unknown device'} ·{' '}
                        {(candidate.strata?.num_channels as number) ?? '?'} lead ·{' '}
                        {(candidate.strata?.sample_rate_hz as number) ?? '?'} Hz
                        {candidate.channel ? ` · ${candidate.channel}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {onOpenAudit && (
                        <button
                          onClick={() => onOpenAudit(candidate.study_id)}
                          className="rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-400 hover:border-slate-500 hover:text-white"
                        >
                          Blinded audit
                        </button>
                      )}
                      <span className="flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-400">
                        <EyeOff className="h-3 w-3" /> submitted label hidden
                      </span>
                    </div>
                  </div>

                  {reveal ? (
                    <div
                      className={`rounded-xl border px-3 py-2 text-xs ${
                        reveal.agreed
                          ? 'border-emerald-800 bg-emerald-950 text-emerald-200'
                          : 'border-amber-800 bg-amber-950 text-amber-200'
                      }`}
                    >
                      {reveal.agreed ? (
                        <>
                          <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                          Both read it as <strong>{reveal.second}</strong>. Admitted to the corpus.
                        </>
                      ) : (
                        <>
                          <ShieldQuestion className="mr-1 inline h-3.5 w-3.5" />
                          You read <strong>{reveal.second}</strong>; the submitter read{' '}
                          <strong>{reveal.submitted}</strong>. Sent for adjudication — a strip two
                          readers label differently is a hard case worth keeping, not a mistake.
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <LabelPicker
                        value={chosen[candidate.id] ?? null}
                        onChange={(label) => setChosen((c) => ({ ...c, [candidate.id]: label }))}
                        disabled={busyId === candidate.id}
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-slate-500">
                          Record your own reading first. The submitter&apos;s is revealed afterwards.
                        </p>
                        <button
                          onClick={() => void recordSecondRead(candidate)}
                          disabled={!chosen[candidate.id] || busyId === candidate.id}
                          className="flex items-center gap-1.5 rounded-lg border border-cyan-700 bg-cyan-800 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {busyId === candidate.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Eye className="h-3.5 w-3.5" />
                          )}
                          Record and reveal
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </section>

          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Gavel className="h-4 w-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-white">
                Disagreements
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {counts.ADJUDICATION ?? 0} to settle
                </span>
              </h3>
            </div>

            {disputed.length === 0 && (
              <p className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-xs text-slate-400">
                Nothing to adjudicate.
              </p>
            )}

            {disputed.map((candidate) => {
              const state = adjudication[candidate.id] ?? { label: null, reason: '' };
              return (
                <div key={candidate.id} className="glass-panel space-y-3 rounded-2xl p-4">
                  <div className="font-mono text-xs text-slate-300">
                    {seconds(candidate.start_seconds)} – {seconds(candidate.end_seconds)}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-slate-300">
                      submitter read <strong className="text-white">{candidate.submitted_label}</strong>
                    </span>
                    <span className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-slate-300">
                      second reader read <strong className="text-white">{candidate.second_label}</strong>
                    </span>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold text-slate-400">
                      Adjudicated label
                    </label>
                    <LabelPicker
                      value={state.label}
                      onChange={(label) =>
                        setAdjudication((a) => ({ ...a, [candidate.id]: { ...state, label } }))
                      }
                      disabled={busyId === candidate.id}
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold text-slate-400">
                      Reason (kept whichever way this goes)
                    </label>
                    <textarea
                      value={state.reason}
                      onChange={(e) =>
                        setAdjudication((a) => ({
                          ...a,
                          [candidate.id]: { ...state, reason: e.target.value },
                        }))
                      }
                      rows={2}
                      className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-200"
                      placeholder="Why this reading, or why neither is usable."
                    />
                  </div>

                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => void settle(candidate, false)}
                      disabled={busyId === candidate.id}
                      className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 hover:border-slate-500 disabled:opacity-40"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => void settle(candidate, true)}
                      disabled={busyId === candidate.id}
                      className="flex items-center gap-1.5 rounded-lg border border-emerald-700 bg-emerald-800 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                    >
                      {busyId === candidate.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      )}
                      Admit
                    </button>
                  </div>
                </div>
              );
            })}
          </section>

          {agreement?.disagreements && Object.keys(agreement.disagreements).length > 0 && (
            <section className="glass-panel space-y-2 rounded-2xl p-4">
              <h3 className="text-sm font-semibold text-white">Where readers differ</h3>
              <p className="text-[11px] text-slate-500">
                The confusion pairs behind the agreement figure. These are the hardest strips in the
                corpus and the most useful ones to keep.
              </p>
              <ul className="space-y-1 text-xs text-slate-300">
                {Object.entries(agreement.disagreements).map(([pair, count]) => (
                  <li key={pair} className="flex justify-between border-b border-slate-800 py-1">
                    <span className="font-mono">{pair}</span>
                    <span className="text-slate-400">{count}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
};
